/**
 * Utilities for managing VTO history in localStorage
 * Updated to use URL-based storage instead of base64 data for better performance
 */

const VTO_HISTORY_KEY = 'vto_history';
const VTO_ENHANCED_STATE_KEY = 'vto_enhanced_state';
const VTO_CONTEXT_KEY = 'vto_context';
const MAX_HISTORY_SIZE = 50;
const MAX_STORAGE_SIZE = 1 * 1024 * 1024; // 1MB limit since we're storing URLs, not base64

export const VTOStorage = {
  /**
   * Save VTO history to localStorage (URLs only, no base64 data)
   * @param {Array} history - Array of VTO results with URLs
   */
  saveHistory: (history) => {
    // Limit history size for performance
    const limitedHistory = Array.isArray(history) 
      ? history.slice(0, MAX_HISTORY_SIZE)
      : [];
    
    // Convert any base64 data to URL references and clean up data
    const cleanedHistory = limitedHistory.map(item => ({
      id: item.id,
      timestamp: item.timestamp || item.createdAt,
      imageUrl: (Array.isArray(item.resultImage) ? item.resultImage[0]?.url : item.resultImage?.url) || item.imageUrl, // Use backend URL
      thumbnailUrl: item.thumbnail?.url || item.thumbnailUrl,
      personImageId: item.personImageId,
      closetClothesId: item.closetClothesId,
      garmentType: item.garmentType,
      garmentSource: item.garmentSource,
      confidence: item.confidence,
      garmentFit: item.garmentFit,
      isFavorite: item.isFavorite,
      clothingItems: (item.clothingItems || []).map(ci => ({
        id: ci.id,
        name: ci.name || ci.clothesName,
        imageUrl: ci.image?.url || ci.imageUrl, // Use backend URL
        type: ci.type
      }))
    }));
    
    console.log('💾 Saving VTO history to localStorage (URLs only):', cleanedHistory.length, 'items');
    
    try {
      const dataString = JSON.stringify(cleanedHistory);
      
      // Check size before saving
      if (dataString.length > MAX_STORAGE_SIZE) {
        console.warn('VTO history data exceeds storage limit, compressing...');
        const compressedHistory = cleanedHistory.slice(0, Math.floor(MAX_HISTORY_SIZE / 2));
        localStorage.setItem(VTO_HISTORY_KEY, JSON.stringify(compressedHistory));
      } else {
        localStorage.setItem(VTO_HISTORY_KEY, dataString);
      }
      
      console.log('✅ VTO history saved successfully (URLs only)');
      return true;
    } catch (error) {
      console.warn('Failed to save VTO history to localStorage:', error);
      
      // If quota exceeded, try aggressive cleanup
      if (error.name === 'QuotaExceededError') {
        console.warn('🚨 localStorage quota exceeded, attempting cleanup...');
        try {
          // Clear all VTO data first
          VTOStorage.clearAllVTOData();
          
          // Try again with only the most recent items
          const minimalHistory = cleanedHistory.slice(0, 5);
          localStorage.setItem(VTO_HISTORY_KEY, JSON.stringify(minimalHistory));
          console.log('✅ Saved minimal VTO history after cleanup');
          return true;
        } catch (retryError) {
          console.error('❌ Still failed to save VTO history after cleanup:', retryError);
          return false;
        }
      }
      return false;
    }
  },

  /**
   * Load VTO history from localStorage
   * @returns {Array} Array of VTO results or empty array
   */
  loadHistory: () => {
    try {
      const stored = localStorage.getItem(VTO_HISTORY_KEY);
      if (!stored) return [];
      
      const parsed = JSON.parse(stored);
      if (!Array.isArray(parsed)) return [];
      
      // Validate and clean up history items
      const validHistory = parsed
        .filter(item => 
          item && 
          typeof item === 'object' && 
          item.id && 
          item.imageUrl && 
          typeof item.timestamp === 'number'
        )
        .slice(0, MAX_HISTORY_SIZE);
      
      return validHistory;
    } catch (error) {
      console.warn('Failed to load VTO history from localStorage:', error);
      // Clear corrupted data
      VTOStorage.clearHistory();
      return [];
    }
  },

  /**
   * Clear VTO history from localStorage
   */
  clearHistory: () => {
    try {
      localStorage.removeItem(VTO_HISTORY_KEY);
      return true;
    } catch (error) {
      console.warn('Failed to clear VTO history from localStorage:', error);
      return false;
    }
  },

  /**
   * Add single VTO result to history
   * @param {Object} result - VTO result object
   */
  addToHistory: (result) => {
    if (!result || !result.id || !result.imageUrl) {
      console.warn('Invalid VTO result provided to addToHistory');
      return false;
    }

    try {
      const currentHistory = VTOStorage.loadHistory();
      
      // Remove duplicate if exists (by ID)
      const filteredHistory = currentHistory.filter(item => item.id !== result.id);
      
      // Add new result at the beginning
      const newHistory = [result, ...filteredHistory].slice(0, MAX_HISTORY_SIZE);
      
      return VTOStorage.saveHistory(newHistory);
    } catch (error) {
      console.warn('Failed to add VTO result to history:', error);
      return false;
    }
  },

  /**
   * Remove specific VTO result from history
   * @param {string} resultId - ID of result to remove
   */
  removeFromHistory: (resultId) => {
    try {
      const currentHistory = VTOStorage.loadHistory();
      const filteredHistory = currentHistory.filter(item => item.id !== resultId);
      
      return VTOStorage.saveHistory(filteredHistory);
    } catch (error) {
      console.warn('Failed to remove VTO result from history:', error);
      return false;
    }
  },

  /**
   * Get storage usage info
   * @returns {Object} Storage usage information
   */
  getStorageInfo: () => {
    try {
      const history = VTOStorage.loadHistory();
      const historySize = JSON.stringify(history).length;
      
      return {
        itemCount: history.length,
        sizeBytes: historySize,
        sizeKB: Math.round(historySize / 1024),
        maxItems: MAX_HISTORY_SIZE
      };
    } catch (error) {
      return {
        itemCount: 0,
        sizeBytes: 0,
        sizeKB: 0,
        maxItems: MAX_HISTORY_SIZE,
        error: error.message
      };
    }
  },

  /**
   * Compress history by removing oldest items
   * @param {number} targetSize - Target number of items to keep
   */
  compressHistory: (targetSize = 25) => {
    try {
      const currentHistory = VTOStorage.loadHistory();
      const compressedHistory = currentHistory.slice(0, Math.max(1, targetSize));
      
      return VTOStorage.saveHistory(compressedHistory);
    } catch (error) {
      console.warn('Failed to compress VTO history:', error);
      return false;
    }
  },

  /**
   * Save enhanced VTO state with coordination across slices (URLs only)
   * @param {Object} enhancedState - Enhanced VTO state object
   */
  saveEnhancedState: (enhancedState) => {
    if (!enhancedState || typeof enhancedState !== 'object') {
      console.warn('Invalid enhanced VTO state provided');
      return false;
    }

    console.log('💾 Saving enhanced VTO state (URLs only):', enhancedState);

    try {
      const stateToSave = {
        history: Array.isArray(enhancedState.history) ? 
          enhancedState.history.slice(0, MAX_HISTORY_SIZE).map(item => ({
            id: item.id,
            imageUrl: (Array.isArray(item.resultImage) ? item.resultImage[0]?.url : item.resultImage?.url) || item.imageUrl,
            thumbnailUrl: item.thumbnail?.url || item.thumbnailUrl,
            timestamp: item.timestamp || item.createdAt,
            personImageId: item.personImageId,
            closetClothesId: item.closetClothesId,
            garmentType: item.garmentType,
            isFavorite: item.isFavorite
          })) : [],
        originalPersonImageUrl: enhancedState.originalPersonImageUrl || null, // URL only
        originalPersonImageId: enhancedState.originalPersonImageId || null, // Backend ID
        activeClothingItems: Array.isArray(enhancedState.activeClothingItems) ? 
          enhancedState.activeClothingItems.map(item => ({
            id: item.id,
            name: item.name || item.clothesName,
            imageUrl: item.image?.url || item.imageUrl, // URL only
            type: item.type
          })) : [],
        currentGeneration: enhancedState.currentGeneration ? {
          id: enhancedState.currentGeneration.id,
          imageUrl: (Array.isArray(enhancedState.currentGeneration.resultImage) ? enhancedState.currentGeneration.resultImage[0]?.url : enhancedState.currentGeneration.resultImage?.url) || enhancedState.currentGeneration.imageUrl,
          thumbnailUrl: enhancedState.currentGeneration.thumbnail?.url,
          timestamp: enhancedState.currentGeneration.timestamp || enhancedState.currentGeneration.createdAt,
          personImageId: enhancedState.currentGeneration.personImageId,
          closetClothesId: enhancedState.currentGeneration.closetClothesId
        } : null,
        lastActivity: enhancedState.lastActivity || Date.now(),
        version: '2.0' // Updated version for URL-based storage
      };

      const dataString = JSON.stringify(stateToSave);
      
      // Check size before saving
      if (dataString.length > MAX_STORAGE_SIZE) {
        console.warn('Enhanced VTO state exceeds storage limit, compressing...');
        const compressedState = {
          ...stateToSave,
          history: stateToSave.history.slice(0, 10), // Keep only 10 most recent
          activeClothingItems: stateToSave.activeClothingItems.slice(0, 5) // Keep only 5 items
        };
        localStorage.setItem(VTO_ENHANCED_STATE_KEY, JSON.stringify(compressedState));
      } else {
        localStorage.setItem(VTO_ENHANCED_STATE_KEY, dataString);
      }
      
      return true;
    } catch (error) {
      console.warn('Failed to save enhanced VTO state:', error);
      
      // Handle quota exceeded by aggressive compression
      if (error.name === 'QuotaExceededError') {
        console.warn('🚨 Enhanced VTO state quota exceeded, attempting compression...');
        try {
          // Clear other data first
          VTOStorage.clearLargeStorageItems();
          
          // Create minimal state with only essential data
          const minimalState = {
            history: [],
            originalPersonImageUrl: enhancedState.originalPersonImageUrl || null,
            originalPersonImageId: enhancedState.originalPersonImageId || null,
            activeClothingItems: Array.isArray(enhancedState.activeClothingItems) ? 
              enhancedState.activeClothingItems.slice(0, 3).map(item => ({
                id: item.id,
                name: item.name || item.clothesName || 'Item'
              })) : [],
            currentGeneration: enhancedState.currentGeneration ? {
              id: enhancedState.currentGeneration.id,
              imageUrl: (Array.isArray(enhancedState.currentGeneration.resultImage) ? enhancedState.currentGeneration.resultImage[0]?.url : enhancedState.currentGeneration.resultImage?.url) || enhancedState.currentGeneration.imageUrl
            } : null,
            lastActivity: Date.now(),
            version: '2.0-minimal'
          };
          
          localStorage.setItem(VTO_ENHANCED_STATE_KEY, JSON.stringify(minimalState));
          console.log('✅ Saved minimal enhanced VTO state');
          return true;
        } catch (retryError) {
          console.error('❌ Failed to save minimal enhanced VTO state:', retryError);
          return false;
        }
      }
      return false;
    }
  },

  /**
   * Load enhanced VTO state (URL-based)
   * @returns {Object|null} Enhanced VTO state or null
   */
  loadEnhancedState: () => {
    try {
      const stored = localStorage.getItem(VTO_ENHANCED_STATE_KEY);
      if (!stored) return null;
      
      const parsed = JSON.parse(stored);
      if (!parsed || typeof parsed !== 'object') return null;
      
      // Handle both old (v1.0) and new (v2.0) formats
      const isOldFormat = !parsed.version || parsed.version.startsWith('1.');
      
      if (isOldFormat) {
        // Convert old format to new format
        console.log('🔄 Converting old VTO state format to URL-based format');
        return {
          history: Array.isArray(parsed.history) ? parsed.history.filter(item => 
            item && typeof item === 'object' && item.id && (item.imageUrl || (Array.isArray(item.resultImage) ? item.resultImage[0]?.url : item.resultImage?.url))
          ).slice(0, MAX_HISTORY_SIZE) : [],
          originalPersonImageUrl: parsed.originalPersonImage || null, // Convert to URL
          originalPersonImageId: null, // Will need to be set from backend
          activeClothingItems: Array.isArray(parsed.activeClothingItems) ? 
            parsed.activeClothingItems.map(item => ({
              id: item.id,
              name: item.name || item.clothesName,
              imageUrl: item.image?.url || item.imageUrl || null,
              type: item.type
            })) : [],
          selectedForTryOn: Array.isArray(parsed.selectedForTryOn) ? parsed.selectedForTryOn : [],
          currentGeneration: parsed.currentGeneration ? {
            id: parsed.currentGeneration.id,
            imageUrl: parsed.currentGeneration.imageUrl || (Array.isArray(parsed.currentGeneration.resultImage) ? parsed.currentGeneration.resultImage[0]?.url : parsed.currentGeneration.resultImage?.url),
            timestamp: parsed.currentGeneration.timestamp || parsed.currentGeneration.createdAt
          } : null,
          lastActivity: parsed.lastActivity || Date.now(),
          version: '2.0-converted'
        };
      }
      
      // New format - validate and clean up the state
      return {
        history: Array.isArray(parsed.history) ? parsed.history.filter(item => 
          item && typeof item === 'object' && item.id && item.imageUrl
        ).slice(0, MAX_HISTORY_SIZE) : [],
        originalPersonImageUrl: parsed.originalPersonImageUrl || null, // WEARER image URL
        originalPersonImageId: parsed.originalPersonImageId || null, // WEARER image backend ID
        activeClothingItems: Array.isArray(parsed.activeClothingItems) ? parsed.activeClothingItems : [], // WEARING items
        selectedForTryOn: Array.isArray(parsed.selectedForTryOn) ? parsed.selectedForTryOn : [],
        currentGeneration: parsed.currentGeneration || null,
        lastActivity: parsed.lastActivity || Date.now(),
        version: parsed.version || '2.0',
        // Enhanced wearer/wearing context
        wearerContext: {
          originalImageUrl: parsed.originalPersonImageUrl,
          originalImageId: parsed.originalPersonImageId,
          currentPersonImageUrl: null,
          isVTOResult: false,
          position: { x: 0, y: 0 },
          scale: 1.0
        },
        wearingContext: {
          activeItems: Array.isArray(parsed.activeClothingItems) ? parsed.activeClothingItems : [],
          selectedIds: Array.isArray(parsed.selectedForTryOn) ? parsed.selectedForTryOn : [],
          itemCount: Array.isArray(parsed.activeClothingItems) ? parsed.activeClothingItems.length : 0
        }
      };
    } catch (error) {
      console.warn('Failed to load enhanced VTO state:', error);
      // Clear corrupted data
      try {
        localStorage.removeItem(VTO_ENHANCED_STATE_KEY);
      } catch (clearError) {
        console.warn('Failed to clear corrupted enhanced VTO state:', clearError);
      }
      return null;
    }
  },

  /**
   * Save VTO context (position, scale, metadata) - URLs only
   * @param {Object} context - VTO context object
   */
  saveVTOContext: (context) => {
    if (!context || typeof context !== 'object') {
      console.warn('Invalid VTO context provided');
      return false;
    }

    try {
      const contextToSave = {
        imageUrl: context.imageUrl || context.image?.url || null, // URL only
        imageId: context.imageId || null, // Backend ID
        metadata: context.metadata || null,
        position: context.position || { x: 0, y: 0 },
        scale: context.scale || 1.0,
        clothingItems: Array.isArray(context.clothingItems) ? 
          context.clothingItems.map(item => ({
            id: item.id,
            name: item.name || item.clothesName,
            imageUrl: item.image?.url || item.imageUrl,
            type: item.type
          })) : [],
        timestamp: context.timestamp || Date.now(),
        sessionId: context.sessionId || `session_${Date.now()}`,
        version: '2.0' // Updated version for URL-based storage
      };

      localStorage.setItem(VTO_CONTEXT_KEY, JSON.stringify(contextToSave));
      return true;
    } catch (error) {
      console.warn('Failed to save VTO context:', error);
      return false;
    }
  },

  /**
   * Load VTO context (URL-based)
   * @returns {Object|null} VTO context or null
   */
  loadVTOContext: () => {
    try {
      const stored = localStorage.getItem(VTO_CONTEXT_KEY);
      if (!stored) return null;
      
      const parsed = JSON.parse(stored);
      if (!parsed || typeof parsed !== 'object') return null;
      
      // Handle both old (v1.0) and new (v2.0) formats
      const isOldFormat = !parsed.version || parsed.version.startsWith('1.');
      
      if (isOldFormat) {
        // Convert old format to new format
        console.log('🔄 Converting old VTO context format to URL-based format');
        return {
          imageUrl: parsed.image || null, // Convert to URL
          imageId: null, // Will need to be set from backend
          metadata: parsed.metadata || null,
          position: parsed.position || { x: 0, y: 0 },
          scale: parsed.scale || 1.0,
          clothingItems: Array.isArray(parsed.clothingItems) ? 
            parsed.clothingItems.map(item => ({
              id: item.id,
              name: item.name || item.clothesName,
              imageUrl: item.image?.url || item.imageUrl || null,
              type: item.type
            })) : [],
          timestamp: parsed.timestamp || Date.now(),
          sessionId: parsed.sessionId || null,
          version: '2.0-converted',
          // Enhanced wearer/wearing context
          wearerImageUrl: parsed.wearerImage || null,
          wearingItems: Array.isArray(parsed.wearingItems) ? parsed.wearingItems : [],
          wearingItemIds: Array.isArray(parsed.wearingItemIds) ? parsed.wearingItemIds : []
        };
      }
      
      return {
        imageUrl: parsed.imageUrl || null, // URL only
        imageId: parsed.imageId || null, // Backend ID
        metadata: parsed.metadata || null,
        position: parsed.position || { x: 0, y: 0 },
        scale: parsed.scale || 1.0,
        clothingItems: Array.isArray(parsed.clothingItems) ? parsed.clothingItems : [],
        timestamp: parsed.timestamp || Date.now(),
        sessionId: parsed.sessionId || null,
        version: parsed.version || '2.0',
        // Enhanced wearer/wearing context
        wearerImageUrl: parsed.wearerImageUrl || null,
        wearingItems: Array.isArray(parsed.wearingItems) ? parsed.wearingItems : [],
        wearingItemIds: Array.isArray(parsed.wearingItemIds) ? parsed.wearingItemIds : []
      };
    } catch (error) {
      console.warn('Failed to load VTO context:', error);
      // Clear corrupted data
      try {
        localStorage.removeItem(VTO_CONTEXT_KEY);
      } catch (clearError) {
        console.warn('Failed to clear corrupted VTO context:', clearError);
      }
      return null;
    }
  },

  /**
   * Handle quota exceeded errors with intelligent cleanup
   * @param {string} key - localStorage key that failed
   * @param {any} data - data that failed to save
   */
  handleQuotaExceeded: (key, data) => {
    try {
      console.log('Handling localStorage quota exceeded for key:', key);
      
      // Clear old VTO data to make space
      VTOStorage.compressHistory(10);
      
      // Try to clear other non-essential VTO data
      try {
        localStorage.removeItem(VTO_CONTEXT_KEY);
      } catch (e) {
        // Ignore errors when clearing
      }
      
      // Try to save the data again with compression if it's an image
      if (typeof data === 'string' && data.startsWith('data:image/')) {
        try {
          // Attempt to compress the image (basic approach)
          const compressedData = VTOStorage.compressImageData(data);
          localStorage.setItem(key, compressedData);
          return true;
        } catch (compressError) {
          console.warn('Failed to save compressed image data:', compressError);
        }
      } else {
        // Try to save the original data again
        try {
          localStorage.setItem(key, typeof data === 'string' ? data : JSON.stringify(data));
          return true;
        } catch (retryError) {
          console.warn('Failed to save data after cleanup:', retryError);
        }
      }
      
      return false;
    } catch (error) {
      console.error('Error handling quota exceeded:', error);
      return false;
    }
  },

  /**
   * Basic image compression for localStorage (synchronous version)
   * @param {string} imageData - Base64 image data
   * @param {number} quality - Compression quality (0.1 to 1.0)
   * @returns {string} Compressed image data or original if compression fails
   */
  compressImageData: (imageData, quality = 0.7) => {
    try {
      if (!imageData || !imageData.startsWith('data:image/')) {
        return imageData;
      }

      // For now, return original data - we can implement async compression later
      // This prevents the Promise issue in handleQuotaExceeded
      return imageData;
    } catch (error) {
      console.warn('Image compression failed:', error);
      return imageData;
    }
  },

  /**
   * Clear all VTO-related localStorage data
   */
  clearAllVTOData: () => {
    try {
      localStorage.removeItem(VTO_HISTORY_KEY);
      localStorage.removeItem(VTO_ENHANCED_STATE_KEY);
      localStorage.removeItem(VTO_CONTEXT_KEY);
      localStorage.removeItem('vto_original_person_image');
      console.log('🧹 Cleared all VTO localStorage data');
      return true;
    } catch (error) {
      console.warn('Failed to clear all VTO data:', error);
      return false;
    }
  },

  /**
   * Clear large localStorage items that might be taking up space
   */
  clearLargeStorageItems: () => {
    try {
      const itemsToCheck = [
        'personImageState',
        'backgroundState', 
        'myClosetState',
        'clothingItemsState'
      ];
      
      itemsToCheck.forEach(key => {
        try {
          const item = localStorage.getItem(key);
          if (item && item.length > 100000) { // If larger than 100KB
            console.log(`🧹 Clearing large localStorage item: ${key} (${Math.round(item.length/1024)}KB)`);
            localStorage.removeItem(key);
          }
        } catch (e) {
          // Ignore individual item errors
        }
      });
      
      return true;
    } catch (error) {
      console.warn('Failed to clear large storage items:', error);
      return false;
    }
  },

  /**
   * Test function to verify VTO storage is working
   */
  testVTOStorage: () => {
    console.log('🧪 Testing VTO Storage...');
    
    // Test basic history saving
    const testHistory = [{
      id: 'test_123',
      imageUrl: 'data:image/jpeg;base64,test',
      timestamp: Date.now(),
      clothingItems: []
    }];
    
    const saveResult = VTOStorage.saveHistory(testHistory);
    console.log('📝 Save test result:', saveResult);
    
    const loadResult = VTOStorage.loadHistory();
    console.log('📖 Load test result:', loadResult);
    
    // Test enhanced state saving with wearer/wearing context
    const testEnhancedState = {
      history: testHistory,
      originalPersonImage: 'data:image/jpeg;base64,original', // WEARER
      activeClothingItems: [{ // WEARING
        id: 'test_clothing_123',
        name: 'Test Garment',
        image: 'data:image/jpeg;base64,garment'
      }],
      selectedForTryOn: ['test_clothing_123'],
      currentGeneration: testHistory[0],
      lastActivity: Date.now(),
      wearerContext: {
        originalImage: 'data:image/jpeg;base64,original',
        currentPersonImage: 'data:image/jpeg;base64,vto_result',
        isVTOResult: true,
        position: { x: 100, y: 50 },
        scale: 1.2
      },
      wearingContext: {
        activeItems: [{ id: 'test_clothing_123', name: 'Test Garment' }],
        selectedIds: ['test_clothing_123'],
        itemCount: 1
      }
    };
    
    const enhancedSaveResult = VTOStorage.saveEnhancedState(testEnhancedState);
    console.log('📝 Enhanced save test result:', enhancedSaveResult);
    
    const enhancedLoadResult = VTOStorage.loadEnhancedState();
    console.log('📖 Enhanced load test result:', enhancedLoadResult);
    
    return {
      basicSave: saveResult,
      basicLoad: loadResult,
      enhancedSave: enhancedSaveResult,
      enhancedLoad: enhancedLoadResult
    };
  },

  /**
   * Get current wearer and wearing information (URL-based)
   * @returns {Object} Current wearer and wearing state
   */
  getWearerAndWearing: () => {
    try {
      const enhancedState = VTOStorage.loadEnhancedState();
      const vtoContext = VTOStorage.loadVTOContext();
      
      return {
        wearer: {
          originalImageUrl: enhancedState?.originalPersonImageUrl || vtoContext?.wearerImageUrl || null,
          originalImageId: enhancedState?.originalPersonImageId || vtoContext?.imageId || null,
          context: enhancedState?.wearerContext || null
        },
        wearing: {
          activeItems: enhancedState?.activeClothingItems || vtoContext?.wearingItems || [],
          selectedIds: enhancedState?.selectedForTryOn || vtoContext?.wearingItemIds || [],
          context: enhancedState?.wearingContext || null
        },
        hasData: !!(enhancedState?.originalPersonImageUrl || enhancedState?.activeClothingItems?.length > 0)
      };
    } catch (error) {
      console.warn('Failed to get wearer and wearing information:', error);
      return {
        wearer: { originalImageUrl: null, originalImageId: null, context: null },
        wearing: { activeItems: [], selectedIds: [], context: null },
        hasData: false
      };
    }
  },

  /**
   * Migrate old base64 storage to URL-based storage
   * @returns {boolean} Success status
   */
  migrateToURLStorage: () => {
    try {
      console.log('🔄 Migrating VTO storage from base64 to URL-based format...');
      
      // Load and convert enhanced state
      const enhancedState = VTOStorage.loadEnhancedState();
      if (enhancedState && enhancedState.version && enhancedState.version.startsWith('1.')) {
        console.log('📦 Converting enhanced state to URL format');
        VTOStorage.saveEnhancedState(enhancedState);
      }
      
      // Load and convert context
      const context = VTOStorage.loadVTOContext();
      if (context && context.version && context.version.startsWith('1.')) {
        console.log('📦 Converting context to URL format');
        VTOStorage.saveVTOContext(context);
      }
      
      // Load and convert history
      const history = VTOStorage.loadHistory();
      if (history && history.length > 0) {
        console.log('📦 Converting history to URL format');
        VTOStorage.saveHistory(history);
      }
      
      console.log('✅ VTO storage migration completed');
      return true;
    } catch (error) {
      console.error('❌ VTO storage migration failed:', error);
      return false;
    }
  }
};

export default VTOStorage;